import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { ChevronDown, DoorOpen } from "lucide-react";

import { useMe } from "@/lib/me";
import { sectionTitle, visibleSections } from "@/lib/nav";
import { useSessionScope } from "@/lib/session-scope";
import { cn } from "@/lib/utils";
import { AppSidebar } from "@/components/app-sidebar";
import { ThemeToggle } from "@/components/theme-toggle";
import { UserMenu } from "@/components/user-menu";

/**
 * Mobile-first frame — a guard holds a phone in one hand at the gate.
 * - Phone: compact app bar (page title + the current gate, tap to switch), content scrolls,
 *   bottom tab bar with a raised Scan button in thumb reach.
 * - Desktop (md+): persistent sidebar; pages use the full width (multi-column grids).
 */
export function AppShell() {
  const { pathname } = useLocation();
  const { scope } = useSessionScope();

  return (
    <div className="flex h-svh w-full bg-muted/40">
      <AppSidebar />

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="pt-safe sticky top-0 z-40 border-b border-border bg-card/95 backdrop-blur">
          <div className="flex h-14 w-full items-center gap-2 px-4 md:px-6">
            <div className="min-w-0 flex-1">
              <h1 className="truncate text-lg leading-tight font-semibold">{sectionTitle(pathname)}</h1>
              {scope && (
                <Link
                  to="/select-gate?switch=1"
                  className="flex max-w-full items-center gap-1 text-xs font-medium text-primary"
                  aria-label={`Gate: ${scope.gate.name}. Switch gate`}
                >
                  <DoorOpen className="size-3.5 shrink-0" />
                  <span className="truncate">{scope.gate.name}</span>
                  <ChevronDown className="size-3 shrink-0" />
                </Link>
              )}
            </div>
            <ThemeToggle />
            <UserMenu />
          </div>
        </header>

        <main className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <Outlet />
        </main>

        <BottomNav />
      </div>
    </div>
  );
}

function BottomNav() {
  const { me } = useMe();
  const items = visibleSections(!!me?.isAdmin);
  return (
    <nav className="pb-safe border-t border-border bg-card/95 backdrop-blur md:hidden" aria-label="Primary">
      <ul className="grid" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
        {items.map(({ href, label, short, icon: Icon, prominent }) => (
          <li key={href}>
            <NavLink
              to={href}
              end={href === "/"}
              className={({ isActive }) =>
                cn(
                  "flex h-16 flex-col items-center justify-center gap-1 text-xs font-medium transition-colors select-none",
                  isActive ? "text-primary" : "text-muted-foreground"
                )
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    className={cn(
                      "relative flex items-center justify-center transition-colors",
                      prominent
                        ? "-mt-7 size-16 rounded-full border-4 border-card bg-primary text-primary-foreground shadow-lg active:scale-95"
                        : cn("h-8 w-14 rounded-full", isActive && "bg-primary/10")
                    )}
                  >
                    <Icon className={cn(prominent ? "size-7" : "size-5")} strokeWidth={isActive ? 2.4 : 1.8} />
                  </span>
                  {short ?? label}
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
